import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import type { GetProductsAPIResponse, Item } from 'typings'
import { useFetch } from './useFetch'
import { SEARCH_TERM_PAGE_SIZE, sortTermById } from './utils'

type SkusReturnType = {
  items: Item[]
  total: number
}

export function getItems(
  items: GetProductsAPIResponse['items'],
  searchTerm?: string,
  itemsPerPage = searchTerm ? SEARCH_TERM_PAGE_SIZE : 10
): Item[] {
  return items
    .reduce<Item[]>(
      (arr, stock) =>
        arr.concat(
          stock.stockKeepingUnitBasicDtoCollection.map((sku) => {
            return {
              name: `${stock.productName} ${sku.skuName ?? ''}`.trim(),
              id: sku.id.toString(),
            }
          })
        ),
      []
    )
    .sort((item) => sortTermById(item.id, searchTerm))
    .slice(0, itemsPerPage)
}

export function useSkus(
  searchTerm?: string,
  options?: UseQueryOptions<SkusReturnType>
) {
  const { fetcherWrapper } = useFetch()

  const page = 1
  const itemsPerPage = searchTerm ? SEARCH_TERM_PAGE_SIZE : 10

  const url = new URL(
    `/api/proxy/catalog_system/pvt/products/GetProducts`,
    window.location.origin
  )

  url.search = new URLSearchParams({
    filter: searchTerm ?? '',
    page: String(page),
    pagesize: String(itemsPerPage),
  }).toString()

  async function fetcher() {
    return fetcherWrapper(url.toString())
      .then((r) => r.json())
      .then((r: GetProductsAPIResponse) => ({
        items: getItems(r.items, searchTerm, itemsPerPage),
        total: r.paging.total,
      }))
  }

  return useQuery({
    queryKey: ['skus', searchTerm],
    queryFn: fetcher,
    ...options,
  })
}
